import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAreas, useFeaturedSection } from '../../../core/api';
import { RemoteImage } from '../../../core/components/RemoteImage';

export interface LocationsSectionConfig {
    itemIds?: string[];
    title?: { en: string; ar: string } | string;
    description?: { en: string; ar: string } | string;
}

interface LocationsSectionProps {
    config?: LocationsSectionConfig;
}

export const LocationsSection = ({ config }: LocationsSectionProps) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const lang = isRTL ? 'ar' : 'en';
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [scrollProgress, setScrollProgress] = React.useState(0);

    // useFeaturedSection only if we don't have a config with specific item selection
    const hasConfigItemIds = config?.itemIds && config.itemIds.length > 0;
    const { data: section } = useFeaturedSection('featured_locations', { enabled: !hasConfigItemIds });

    // Determine effective config
    const itemIds: string[] | undefined = hasConfigItemIds ? config!.itemIds : (section?.itemIds && section.itemIds.length > 0 ? section.itemIds : undefined);
    const rawTitle = config?.title || section?.title;
    const title = typeof rawTitle === 'object' ? rawTitle[lang] : rawTitle;

    const rawDesc = config?.description || section?.description;
    const description = typeof rawDesc === 'object' ? rawDesc[lang] : rawDesc;

    // Always fetch all areas, filter client-side
    const { data: areas, isLoading } = useAreas({ enabled: true });

    // Apply client-side filtering when specific IDs are selected, preserving selection order
    const locationList = React.useMemo(() => {
        const all = areas || [];
        if (!itemIds || itemIds.length === 0) return all;
        return itemIds
            .map(id => all.find(a => a.id === id))
            .filter(Boolean) as typeof all;
    }, [areas, itemIds]);


    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            const scrollRange = scrollWidth - clientWidth;
            const progress = (scrollLeft / scrollRange) * 100;
            setScrollProgress(progress);
        }
    };

    const handleIndicatorClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!scrollRef.current) return;
        const progressBar = e.currentTarget;
        const clickPosition = e.nativeEvent.offsetX;
        const totalWidth = progressBar.clientWidth;
        const percentage = clickPosition / totalWidth;

        const { scrollWidth, clientWidth } = scrollRef.current;
        const scrollableWidth = scrollWidth - clientWidth;

        scrollRef.current.scrollTo({
            left: scrollableWidth * percentage,
            behavior: 'smooth'
        });
    };

    if (!isLoading && locationList.length === 0) return null;

    return (
        <section className="py-6 sm:py-12 bg-slate-50 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
                {/* Section Header */}
                <div className="text-center mb-4 sm:mb-8">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="text-3xl sm:text-5xl font-display font-bold text-secondary mb-4"
                    >
                        {title || t('locations.title', 'Our Locations')}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-slate-500 text-lg sm:text-xl max-w-2xl mx-auto font-light"
                    >
                        {description || t('locations.description', 'Explore our landmarks in strategic cities.')}
                    </motion.p>
                </div>

                {/* Loading state */}
                {isLoading && (
                    <div className="flex items-center justify-between py-8">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex flex-col items-center gap-4">
                                <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-full bg-slate-200 animate-pulse" />
                                <div className="w-24 h-4 bg-slate-200 animate-pulse rounded" />
                            </div>
                        ))}
                    </div>
                )}

                {/* Timeline / Connected Pins Layout */}
                {!isLoading && locationList.length > 0 && (
                    <div className="relative">
                        <div
                            ref={scrollRef}
                            onScroll={handleScroll}
                            className="flex items-start md:items-center justify-between overflow-x-auto md:overflow-visible pb-4 pt-4 px-4 sm:px-0 scrollbar-hide snap-x snap-mandatory w-full"
                        >
                            {locationList.map((location, index) => (
                                <React.Fragment key={location.id}>
                                    {/* Location Item */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: index * 0.2 }}
                                        className="flex-shrink-0 w-[140px] sm:w-[180px] md:w-auto flex flex-col items-center relative group snap-center z-10"
                                    >
                                        <Link to={`/areas/${location.slug}`} className="block">
                                            {/* Pin Icon Wrapper */}
                                            <div className="w-20 h-20 sm:w-32 sm:h-32 bg-white rounded-full border-4 border-slate-100 shadow-xl overflow-hidden mb-6 z-10 group-hover:scale-110 transition-transform duration-300 group-hover:border-secondary/20 relative">
                                                <RemoteImage
                                                    src={location.image}
                                                    alt={location.name || location.slug}
                                                    containerClassName="absolute inset-0 w-full h-full"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Location Title */}
                                            <h3 className="text-lg sm:text-2xl font-bold text-secondary text-center group-hover:text-accent transition-colors">
                                                {location.name || location.slug}
                                            </h3>
                                        </Link>
                                    </motion.div>

                                    {/* Connector (Between Items) */}
                                    {index !== locationList.length - 1 && (
                                        <div className="flex-grow min-w-[60px] sm:min-w-[100px] h-24 relative -mt-8 md:-mt-12 -mx-4 md:mx-0 flex items-center justify-center pointer-events-none opacity-60">
                                            <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 24">
                                                <path
                                                    d={index % 2 === 0
                                                        ? "M0,12 C30,22 70,2 100,12"
                                                        : "M0,12 C30,2 70,22 100,12"
                                                    }
                                                    fill="none"
                                                    stroke="#94a3b8"
                                                    strokeWidth="2"
                                                    strokeDasharray="4 4"
                                                    vectorEffect="non-scaling-stroke"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Custom Scroll Indicator (Mobile only) */}
                        <div
                            className="md:hidden w-full max-w-[200px] h-1.5 bg-slate-200 rounded-full mx-auto mt-6 overflow-hidden cursor-pointer relative"
                            onClick={handleIndicatorClick}
                        >
                            <div
                                className="absolute top-0 h-full bg-secondary rounded-full transition-transform duration-100 ease-out"
                                style={{
                                    width: `${Math.max(20, 100 / locationList.length)}%`,
                                    transform: `translateX(${scrollProgress * 2}%)`
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};
